import { useEffect, useState } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconCheck, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useAuth } from '../../auth/useAuth';
import { useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';

interface BankAccount {
  id: number;
  name: string;
}

interface Receipt {
  id: number;
  receipt_number: string;
  payer_name: string | null;
  paid_amount: string;
  payment_date: string;
}

interface BankDeposit {
  id: number;
  reference: string;
  deposit_date: string;
  status: 'draft' | 'deposited';
  bank_account: BankAccount;
  total_amount: number;
  created_by: { id: number; full_name: string } | null;
  deposited_by: { id: number; full_name: string } | null;
  lines: { id: number; receipt: Receipt }[];
}

export function BankDeposits() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError, showSuccess } = useToast();

  const [deposits, setDeposits] = useState<BankDeposit[] | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [creating, setCreating] = useState(false);
  const [bankAccountId, setBankAccountId] = useState('');
  const [depositDate, setDepositDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canCreate = can('banking.create');
  const canConfigure = can('banking.configure');
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const load = () => api.get<BankDeposit[]>('/bank-deposits').then((res) => setDeposits(res.data));

  useEffect(() => {
    load();
    api.get<BankAccount[]>('/bank-accounts').then((res) => setBankAccounts(res.data));
  }, []);

  useEffect(() => {
    if (!creating) return;
    api
      .get<{ data: Receipt[] }>('/receipts', { params: { undeposited: true, per_page: 100 } })
      .then((res) => setReceipts(res.data.data));
  }, [creating]);

  const openNew = () => {
    setBankAccountId(bankAccounts[0] ? String(bankAccounts[0].id) : '');
    setDepositDate(new Date().toISOString().slice(0, 10));
    setSelected(new Set());
    setError('');
    setCreating(true);
  };

  const toggleReceipt = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotal = receipts.filter((r) => selected.has(r.id)).reduce((sum, r) => sum + Number(r.paid_amount), 0);

  const save = async () => {
    if (selected.size === 0) {
      setError('Select at least one receipt.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/bank-deposits', {
        bank_account_id: bankAccountId,
        deposit_date: depositDate,
        receipt_ids: Array.from(selected),
      });
      setCreating(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const record = async (deposit: BankDeposit) => {
    try {
      await api.post(`/bank-deposits/${deposit.id}/record`);
      showSuccess('Deposit recorded as banked.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'You may not record a deposit you created yourself.');
    }
  };

  const remove = async (deposit: BankDeposit) => {
    if (!(await confirmDelete(`Delete draft deposit "${deposit.reference}"?`))) return;
    try {
      await api.delete(`/bank-deposits/${deposit.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Bank Deposits</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openNew} disabled={bankAccounts.length === 0}>
            + New deposit slip
          </button>
        )}
      </header>
      <p className="muted">Groups receipts banked together, so a bank statement line can be matched against their combined total.</p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Bank Account</th>
                <th>Deposit Date</th>
                <th>Receipts</th>
                <th className="right">Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deposits?.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.reference}</strong>
                  </td>
                  <td>{d.bank_account.name}</td>
                  <td>{d.deposit_date}</td>
                  <td>{d.lines.length}</td>
                  <td className="right">{fmtMoney(d.total_amount)}</td>
                  <td>
                    <span className={`badge ${d.status === 'deposited' ? 'badge-active' : 'badge-neutral'}`}>
                      {d.status === 'deposited' ? 'Deposited' : 'Draft'}
                    </span>
                  </td>
                  <td className="right">
                    {d.status === 'draft' && (
                      <>
                        {canConfigure && <IconButton icon={IconCheck} label="Record as banked" onClick={() => record(d)} />}
                        {canCreate && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(d)} />}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {deposits?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No deposit slips yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <Modal title="New deposit slip" onClose={() => setCreating(false)}>
          <div className="form-grid">
            {error && <div className="alert alert-error span-2">{error}</div>}
            <label>
              Bank account
              <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Deposit date
              <input type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} />
            </label>
            <div className="span-2">
              <h3>Receipts not yet deposited</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Receipt #</th>
                      <th>Payer</th>
                      <th>Date</th>
                      <th className="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleReceipt(r.id)} />
                        </td>
                        <td>{r.receipt_number}</td>
                        <td>{r.payer_name ?? '—'}</td>
                        <td>{r.payment_date}</td>
                        <td className="right">{fmtMoney(Number(r.paid_amount))}</td>
                      </tr>
                    ))}
                    {receipts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="muted center">
                          No undeposited receipts.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p>
                <strong>Selected total: {fmtMoney(selectedTotal)}</strong>
              </p>
            </div>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
